import React, { useRef } from 'react';
import FlagIcon from './FlagIcon';

const POS_LABELS = ['1st','2nd','3rd','4th'];
const POS_CLASSES = ['pos-1','pos-2','pos-3','pos-4'];

export default function GroupStage({ groups, teamsByGroup, picks, onUpdate, locked }) {
  const dragItem     = useRef(null);
  const dragOverItem = useRef(null);

  // ── Desktop drag-and-drop ──────────────────────────────────────────────────
  const handleDragStart = (group, idx) => { dragItem.current = { group, idx }; };
  const handleDragEnter = (group, idx) => { dragOverItem.current = { group, idx }; };

  const handleDrop = (group) => {
    if (!dragItem.current || dragItem.current.group !== group) return;
    const order = [...(picks[group] || teamsByGroup(group).map(t => t.id))];
    const from = dragItem.current.idx;
    const to   = dragOverItem.current?.idx ?? from;
    if (from === to) return;
    const [moved] = order.splice(from, 1);
    order.splice(to, 0, moved);
    onUpdate(group, order);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  // ── Mobile up/down buttons ─────────────────────────────────────────────────
  const moveTeam = (group, idx, dir) => {
    const order = [...(picks[group] || teamsByGroup(group).map(t => t.id))];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    onUpdate(group, order);
  };

  return (
    <div>
      <p className="text-muted mb-2">
        Rank each team 1st–4th in their group. Top 2 advance automatically;
        the best 8 third-place teams also advance.
      </p>
      <div className="groups-grid">
        {groups.map(g => {
          const gTeams = teamsByGroup(g);
          const order  = picks[g] || gTeams.map(t => t.id);
          return (
            <div key={g} className="group-card">
              <h3>Group {g}</h3>
              {order.map((teamId, idx) => {
                const team = gTeams.find(t => t.id === teamId);
                if (!team) return null;
                return (
                  <div
                    key={teamId}
                    className="group-team-row"
                    draggable={!locked}
                    onDragStart={() => handleDragStart(g, idx)}
                    onDragEnter={() => handleDragEnter(g, idx)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => handleDrop(g)}
                    style={{ userSelect: 'none' }}
                  >
                    <span className={`pos-badge ${POS_CLASSES[idx]}`}>{idx + 1}</span>
                    <FlagIcon code={team.country_code} size="1.2em" />
                    <span className="team-name">{team.name}</span>

                    {!locked && (
                      <div style={btnGroupStyle}>
                        <button
                          onMouseDown={e => e.stopPropagation()}
                          onClick={e => { e.stopPropagation(); moveTeam(g, idx, -1); }}
                          disabled={idx === 0}
                          style={arrowBtnStyle(idx === 0)}
                          aria-label="Move up"
                        >▲</button>
                        <button
                          onMouseDown={e => e.stopPropagation()}
                          onClick={e => { e.stopPropagation(); moveTeam(g, idx, 1); }}
                          disabled={idx === order.length - 1}
                          style={arrowBtnStyle(idx === order.length - 1)}
                          aria-label="Move down"
                        >▼</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const btnGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  marginLeft: 'auto',
  gap: '2px',
};

const arrowBtnStyle = (disabled) => ({
  display: 'block',
  width: '36px',
  height: '28px',
  lineHeight: '28px',
  padding: 0,
  fontSize: '14px',
  background: disabled ? 'transparent' : 'var(--navy-light)',
  color: disabled ? 'var(--border)' : 'var(--text)',
  border: `1px solid ${disabled ? 'transparent' : 'var(--border)'}`,
  borderRadius: '4px',
  cursor: disabled ? 'default' : 'pointer',
  textAlign: 'center',
  flexShrink: 0,
});
